import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';

import EventFeedbackDialog from './EventFeedbackDialog';
import GoogleReviewModal from '../ui/GoogleReviewModel';
import { useEventFeedback } from '../../hooks/eventFeedbackHooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../../utils/logger';

interface FeedbackData {
  uuid: string | null;
  generalRating: number | null;
  feedbackInputText: string;
}

export default function EventFeedbackDialogContainer() {
  const { user } = useSelector((state: RootState) => state.auth);
  const { buildingConfig } = useSelector((state: any) => state.settings || { buildingConfig: {} });
  
  const {
    pendingFeedback,
    isLoading,
    updateFeedback,
    updateLastReviewed,
    didNotAttend,
  } = useEventFeedback(user?.uuid);

  const [showFeedbackDialog, setShowFeedbackDialog] = useState(true);
  const [showGoogleReview, setShowGoogleReview] = useState(false);
  const [feedbackData, setFeedbackData] = useState<FeedbackData>({
    uuid: null,
    generalRating: null,
    feedbackInputText: '',
  });

  const nowDateTime = dayjs().toISOString();

  // Show feedback dialog when pending feedback (or mock) is available
  useEffect(() => {
    if (pendingFeedback && !isLoading) {
      setShowFeedbackDialog(true);
      setFeedbackData(prev => ({
        ...prev,
        uuid: pendingFeedback.uuid,
      }));

      // Track analytics
      /* ANALYTICS_DISABLED: EVENT_FEEDBACK_DIALOG_SHOWN ('event_feedback_dialog_shown')
        analyticsService.trackEvent(AnalyticsEvent.EVENT_FEEDBACK_DIALOG_SHOWN, {
                eventUuid: pendingFeedback.uuid,
                isCreator: pendingFeedback.isCreator,
              });
        */
    }
  }, [pendingFeedback, isLoading]);

  const handleFacePress = useCallback((eventFeedbackUuid: string, generalRating: number) => {
    setFeedbackData(prev => ({
      ...prev,
      uuid: eventFeedbackUuid,
      generalRating,
    }));

    analyticsService.trackEvent(AnalyticsEvent.EVENT_FEEDBACK_RATING_SELECTED, {
      rating: generalRating,
      eventUuid: eventFeedbackUuid,
    });
  }, []);

  const handleChangeFeedbackInputText = useCallback((newValue: string) => {
    setFeedbackData(prev => ({
      ...prev,
      feedbackInputText: newValue,
    }));
  }, []);

  const handleDidNotAttendPress = useCallback(async (eventFeedbackUuid: string, type: 'groupEvent' | 'post') => {
    try {
      await didNotAttend(eventFeedbackUuid, type);
      setShowFeedbackDialog(false);
      setShowGoogleReview(true);
    } catch (error) {
      logger.error('Error marking did not attend:', error);
    }
  }, [didNotAttend]);

  const handleSubmitEventFeedback = useCallback(async (type: 'groupEvent' | 'post') => {
    const { uuid, generalRating, feedbackInputText } = feedbackData;
    
    if (!uuid || !generalRating) return;

    try {
      await updateFeedback({
        uuid,
        generalRating,
        feedbackInputText,
        type,
      });

      setShowFeedbackDialog(false);
      setShowGoogleReview(true);
    } catch (error) {
      logger.error('Error submitting feedback:', error);
    }
  }, [feedbackData, updateFeedback]);

  const shouldShowGoogleReview = useCallback(() => {
    if (!buildingConfig?.showGoogleReview || !showGoogleReview || !user?.building?.reviewUrl) {
      return false;
    }

    if (!user.lastReviewedAt) {
      return true;
    }

    const nextReviewDate = dayjs(user.lastReviewedAt).add(90, 'days');
    return dayjs().isAfter(nextReviewDate);
  }, [buildingConfig?.showGoogleReview, showGoogleReview, user?.building?.reviewUrl, user?.lastReviewedAt]);

  const handleGoogleReviewClose = useCallback(() => {
    setShowGoogleReview(false);
  }, []);

  const handleUpdateLastReviewed = useCallback(async () => {
    try {
      await updateLastReviewed(nowDateTime);
    } catch (error) {
      logger.error('Error updating last reviewed timestamp:', error);
    }
  }, [updateLastReviewed, nowDateTime]);

  // Don't render anything if there's no pending feedback
  if (!pendingFeedback || isLoading) {
    return null;
  }

  return (
    <>
      <EventFeedbackDialog
        visible={showFeedbackDialog}
        feedback={pendingFeedback}
        onFacePress={handleFacePress}
        onDidNotAttendPress={handleDidNotAttendPress}
        onSubmitEventFeedback={handleSubmitEventFeedback}
        generalRating={feedbackData.generalRating}
        feedbackInputText={feedbackData.feedbackInputText}
        onChangeFeedbackInputText={handleChangeFeedbackInputText}
        onClose={() => setShowFeedbackDialog(false)}
      />

      {shouldShowGoogleReview() && user?.building?.reviewUrl && user?.building?.name && (
        <GoogleReviewModal
          visible={showGoogleReview}
          onClose={handleGoogleReviewClose}
          reviewUrl={user.building.reviewUrl}
          buildingName={user.building.name}
          onUpdateLastReviewed={handleUpdateLastReviewed}
        />
      )}
    </>
  );
}
